import { useEffect, useMemo, useState } from 'react'
import { getNotificationPrefs, updateNotificationPrefs, type NotificationPrefs } from '../../api/notifications'
import { getToken } from '../../lib/auth'

export default function NotificationsSettings() {
  const authed = Boolean(getToken())
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [timezoneInput, setTimezoneInput] = useState('UTC')
  const [quietStartInput, setQuietStartInput] = useState('')
  const [quietEndInput, setQuietEndInput] = useState('')
  const [quietError, setQuietError] = useState<string | null>(null)

  useEffect(() => {
    if (!authed) return
    setLoading(true)
    getNotificationPrefs()
      .then((p) => {
        setPrefs(p)
        setTimezoneInput(p.timezone || 'UTC')
        setQuietStartInput(formatTimeDisplay(p.quiet_hours_start))
        setQuietEndInput(formatTimeDisplay(p.quiet_hours_end))
      })
      .catch((e)=> setError(e?.response?.data?.detail || e?.message || 'Failed to load preferences'))
      .finally(()=> setLoading(false))
  }, [authed])

  async function applyPatch(patch: Partial<NotificationPrefs>) {
    if (!prefs) return
    setSaving(true)
    setMsg(null)
    const nextLocal = { ...prefs, ...patch }
    setPrefs(nextLocal)
    try {
      const next = await updateNotificationPrefs(patch)
      setPrefs(next)
      setMsg('Saved')
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Save failed')
      // optimistic fallback already applied visually
    } finally {
      setSaving(false)
    }
  }

  function onToggle(key: keyof NotificationPrefs) {
    if (!prefs) return
    const value = !prefs[key]
    applyPatch({ [key]: value } as any)
  }

  function onTimezoneBlur() {
    const tz = (timezoneInput || 'UTC').trim()
    if (!prefs) return
    if (tz === (prefs.timezone || '')) return
    applyPatch({ timezone: tz })
  }

  function isValidHHMM(value: string) {
    return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value)
  }

  function applyQuietHour(field: 'quiet_hours_start' | 'quiet_hours_end', value: string) {
    setQuietError(null)
    const trimmed = value.trim()
    if (!trimmed) {
      applyPatch(field === 'quiet_hours_start' ? { quiet_hours_start: null } : { quiet_hours_end: null })
      return
    }
    if (!isValidHHMM(trimmed)) {
      setQuietError('Use 24h format, e.g., 22:30')
      return
    }
    const payload = field === 'quiet_hours_start' ? { quiet_hours_start: `${trimmed}:00` } : { quiet_hours_end: `${trimmed}:00` }
    applyPatch(payload)
  }

  function clearQuietHours() {
    setQuietStartInput('')
    setQuietEndInput('')
    applyPatch({ quiet_hours_start: null, quiet_hours_end: null })
  }

  const smsDisabled = useMemo(() => !prefs?.sms_opted_in, [prefs])

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-extrabold">Notifications</h1>
        <p className="text-white/70 text-sm">Choose how and when you want to be notified. Quiet hours and timezone help reduce noise.</p>
      </div>
      {loading && <div className="rounded-xl border border-white/10 p-4">Loading…</div>}
      {error && <div className="rounded-xl border border-white/10 p-4 text-red-400">{error}</div>}
      {prefs && (
        <div className="grid grid-cols-1 gap-6">
          <section className="space-y-3">
            <h2 className="text-white/85 font-bold">Email</h2>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 divide-y divide-white/10">
              {(['email_system','email_product','email_commerce','email_marketing'] as Array<keyof NotificationPrefs>).map((k) => (
                <Row key={k} title={labelize(k)} description={descFor(k)} disabled={prefs.email_unsub_all}>
                  <input type="checkbox" checked={Boolean((prefs as any)[k])} onChange={()=>onToggle(k)} disabled={prefs.email_unsub_all} />
                </Row>
              ))}
              <Row title="Unsubscribe from all emails" description="Overrides individual email categories.">
                <input type="checkbox" checked={prefs.email_unsub_all} onChange={()=>onToggle('email_unsub_all')} />
              </Row>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-white/85 font-bold">SMS</h2>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 divide-y divide-white/10">
              <Row title="SMS opt-in" description="Allow Pixeloop to send you text messages about your activity.">
                <input type="checkbox" checked={prefs.sms_opted_in} onChange={()=>onToggle('sms_opted_in')} />
              </Row>
              {prefs.sms_opted_in && prefs.sms_optin_timestamp && (
                <div className="pt-2 text-xs text-white/60">Opted in {new Date(prefs.sms_optin_timestamp).toLocaleString()}</div>
              )}
              {(['sms_system','sms_product','sms_commerce','sms_marketing'] as Array<keyof NotificationPrefs>).map((k) => (
                <Row key={k} title={labelize(k)} description={descFor(k)} disabled={smsDisabled}>
                  <input type="checkbox" checked={Boolean((prefs as any)[k])} onChange={()=>onToggle(k)} disabled={smsDisabled} />
                </Row>
              ))}
              {smsDisabled && (
                <div className="px-1 pt-2 text-xs text-white/60">Enable SMS opt-in to control individual SMS categories.</div>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-white/85 font-bold">Quiet hours</h2>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-3">
              <p className="text-white/75 text-sm">Mute email and SMS during specific hours. Times use your local timezone.</p>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                <TimeField label="Start" value={quietStartInput} onChange={setQuietStartInput} onBlur={() => applyQuietHour('quiet_hours_start', quietStartInput)} />
                <TimeField label="End" value={quietEndInput} onChange={setQuietEndInput} onBlur={() => applyQuietHour('quiet_hours_end', quietEndInput)} />
              </div>
              {quietError && <div className="text-xs text-red-400">{quietError}</div>}
              <button onClick={clearQuietHours} className="px-3 py-2 rounded-lg border border-white/15 text-white/90 font-semibold hover:bg-white/5 w-max">Clear quiet hours</button>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-white/85 font-bold">Daily digest</h2>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 divide-y divide-white/10">
              <Row title="Morning digest" description="Receive a daily summary of low-priority notifications.">
                <input type="checkbox" checked={prefs.daily_digest_enabled} onChange={()=>onToggle('daily_digest_enabled')} />
              </Row>
              {prefs.last_digest_sent_at && (
                <div className="pt-2 text-xs text-white/60">Last sent {new Date(prefs.last_digest_sent_at).toLocaleString()}</div>
              )}
            </div>
          </section>

          <section className="space-y-3 max-w-md">
            <h2 className="text-white/85 font-bold">Timezone</h2>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="text-white/75 text-sm mb-2">Used for quiet hours and digest scheduling.</div>
              <input
                value={timezoneInput}
                onChange={(e)=>setTimezoneInput(e.target.value)}
                onBlur={onTimezoneBlur}
                placeholder="e.g. America/Los_Angeles"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none"
              />
            </div>
          </section>
        </div>
      )}
      {(msg || saving) && (
        <div className="rounded-lg border border-white/10 p-2 text-sm text-white/85">{saving ? 'Saving changes…' : msg}</div>
      )}
    </div>
  )
}

function labelize(key: keyof NotificationPrefs) {
  return key
    .replace(/^email_/, '')
    .replace(/^sms_/, '')
    .replaceAll('_', ' ')
    .replace(/^(.)/, (m) => m.toUpperCase())
}

function descFor(key: keyof NotificationPrefs) {
  if (key.endsWith('system')) return 'Security alerts, login codes, wallet activity.'
  if (key.endsWith('product')) return 'Comments, mentions, follows, and other updates.'
  if (key.endsWith('commerce')) return 'Orders, invoices, and refunds.'
  if (key.endsWith('marketing')) return 'Product news and promotions.'
  return undefined
}

function TimeField({ label, value, onChange, onBlur }: { label: string; value: string; onChange: (v: string) => void; onBlur: () => void }) {
  return (
    <label className="block">
      <div className="text-white/70 mb-1">{label}</div>
      <input
        value={value}
        onChange={(e)=>onChange(e.target.value)}
        onBlur={onBlur}
        placeholder="22:00"
        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none"
      />
    </label>
  )
}

function Row({ title, description, children, disabled }: { title: string; description?: string; children: React.ReactNode; disabled?: boolean }) {
  return (
    <div className="py-2" style={{ opacity: disabled ? 0.6 : 1 }}>
      <div className="flex items-center">
        <div className="flex-1 pr-3 min-w-0">
          <div className="text-white font-semibold truncate">{title}</div>
          {description ? <div className="text-xs text-white/65 mt-1">{description}</div> : null}
        </div>
        {children}
      </div>
    </div>
  )
}

function formatTimeDisplay(value?: string | null) {
  if (!value) return ''
  const parts = value.split(':')
  if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`
  return value
}
